import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from '@/hooks/useAuth';

const STORAGE_KEY_PREFIX = 'sb_unread_comment_tasks_';

interface UnreadCommentsContextType {
  tasksWithUnreadComments: Set<string>;
  hasUnreadComments: (taskId: string) => boolean;
  addUnreadCommentTask: (taskId: string) => void;
  markTaskCommentsAsRead: (taskId: string) => void;
}

const UnreadCommentsContext = createContext<
  UnreadCommentsContextType | undefined
>(undefined);

export function UnreadCommentsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tasksWithUnreadComments, setTasksWithUnreadComments] = useState<
    Set<string>
  >(new Set());

  const storageKey = user?.id ? `${STORAGE_KEY_PREFIX}${user.id}` : null;

  // Load from localStorage on mount or user change
  useEffect(() => {
    if (!storageKey) {
      setTasksWithUnreadComments(new Set());
      return;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        setTasksWithUnreadComments(new Set(Array.isArray(ids) ? ids : []));
      } else {
        setTasksWithUnreadComments(new Set());
      }
    } catch {
      setTasksWithUnreadComments(new Set());
    }
  }, [storageKey]);

  // Persist to localStorage when set changes
  useEffect(() => {
    if (!storageKey) return;
    try {
      const ids = Array.from(tasksWithUnreadComments);
      if (ids.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(ids));
      }
    } catch (e) {
      console.error('Failed to persist unread comment tasks:', e);
    }
  }, [storageKey, tasksWithUnreadComments]);

  const hasUnreadComments = useCallback(
    (taskId: string) => tasksWithUnreadComments.has(taskId),
    [tasksWithUnreadComments]
  );

  const addUnreadCommentTask = useCallback((taskId: string) => {
    setTasksWithUnreadComments(prev => new Set(prev).add(taskId));
  }, []);

  const markTaskCommentsAsRead = useCallback((taskId: string) => {
    setTasksWithUnreadComments(prev => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  }, []);

  return (
    <UnreadCommentsContext.Provider
      value={{
        tasksWithUnreadComments,
        hasUnreadComments,
        addUnreadCommentTask,
        markTaskCommentsAsRead,
      }}
    >
      {children}
    </UnreadCommentsContext.Provider>
  );
}

export function useUnreadComments() {
  const context = useContext(UnreadCommentsContext);
  if (context === undefined) {
    throw new Error(
      'useUnreadComments must be used within an UnreadCommentsProvider'
    );
  }
  return context;
}
